import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule, CurrencyPipe, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FormsModule } from '@angular/forms';

import { Bundle, ServiceRequest, Patient, Medication, ChargeItemDefinition, Invoice, ChargeItem } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { ClinicalPricingService, PricingCalculationResult } from '../shared/services/clinical-pricing.service';
import { StateService } from '../shared/state.service';
import { UtilityService } from '../shared/utility.service';
import { AuthService } from '../shared/auth/auth.service';
import { TimeService } from '../services/time.service';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { ChipsDirective } from '../chips.directive';
import { NaPipe } from '../shared/na.pipe';

export interface ServiceMarginRow {
  serviceRequest: ServiceRequest;
  patientId: string;
  patientName: string;
  code: string;
  display: string;
  type: string;
  costPrice: number;
  sellingPrice: number;
  profit: number;
  currency: string;
  calcDetails: PricingCalculationResult;
}

export interface ProductMarginRow {
  medication: Medication;
  name: string;
  form: string;
  manufacturer: string;
  unitsSold: number;
  costPrice: number;
  sellingPrice: number;
  profit: number;
  currency: string;
}

@Component({
  selector: 'app-profit-margins',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatChipsModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    CustomTabsComponent,
    ChipsDirective,
    NaPipe,
    CurrencyPipe,
    TitleCasePipe
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './profit-margins.component.html',
  styleUrl: './profit-margins.component.scss'
})
export class ProfitMarginsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly clinicalPricingService = inject(ClinicalPricingService);
  private readonly stateService = inject(StateService);
  private readonly utilityService = inject(UtilityService);
  private readonly auth = inject(AuthService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly timeService = inject(TimeService);
  private readonly patientNamePipe = new PatientNamePipe();

  // Active Tab: 'services' or 'products'
  activeTabId = 'services';
  tabs: TabItem[] = [
    { id: 'services', label: 'Completed Services' },
    { id: 'products', label: 'Pharmacy Products' }
  ];

  // Completed Services Data
  servicesLoading = false;
  servicesRows: ServiceMarginRow[] = [];
  servicesDisplayedColumns = ['serviceName', 'patient', 'costPrice', 'sellingPrice', 'profitMargin'];

  // Completed Services Aggregates
  totalServicesCount = 0;
  totalServicesCost = 0;
  totalServicesRevenue = 0;
  totalServicesProfit = 0;

  // Pharmacy Products Data
  productsLoading = false;
  productsRows: ProductMarginRow[] = [];
  productsDisplayedColumns = ['productName', 'stock', 'costPrice', 'sellingPrice', 'profitMargin'];

  // Pharmacy Products Aggregates
  totalProductsCount = 0;
  totalProductsSoldUnits = 0;
  totalProductsValuationCost = 0;
  totalProductsValuationRevenue = 0;
  totalProductsValuationProfit = 0;

  // Patient Cache to optimize requests
  private readonly patientCache = new Map<string, Patient>();

  // Date Period Filtering (Products Tab)
  periodPresets = [
    { id: 'today', label: 'Today',   days: 0 },
    { id: '7d',  label: '7 Days',   days: 7 },
    { id: '30d', label: '30 Days',  days: 30 },
    { id: '90d', label: '90 Days',  days: 90 },
    { id: '6m',  label: '6 Months', days: 180 },
    { id: '1y',  label: '1 Year',   days: 365 },
    { id: 'all', label: 'All Time', days: 0 },
  ];
  activePeriodPreset = '30d';
  periodStartDate: Date = this.timeService.getTrueDate();
  periodEndDate: Date = this.timeService.getTrueDate();
  showCustomRange = false;
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;

  ngOnInit() {
    console.log('ProfitMarginsComponent initialized');
    this.computeDateRange();
    // Load services data by default
    this.loadCompletedServices();
  }

  onTabChanged(tabId: string) {
    this.activeTabId = tabId;
    if (tabId === 'services') {
      this.loadCompletedServices();
    } else if (tabId === 'products') {
      this.loadPharmacyProducts();
    }
  }

  /**
   * Load completed ServiceRequests and run calculations
   */
  loadCompletedServices() {
    if (this.servicesLoading) return;
    this.servicesLoading = true;
    this.servicesRows = [];
    this.cdRef.markForCheck();

    const dateParams = this.activePeriodPreset !== 'all'
      ? `&_lastUpdated=ge${this.toFhirDate(this.periodStartDate)}&_lastUpdated=le${this.toFhirDate(this.periodEndDate)}`
      : '';
    this.http.get<Bundle<ServiceRequest>>(`${this.backendEndPoint}/ServiceRequest?status=completed${dateParams}&_count=1000`)
      .pipe(
        switchMap(bundle => {
          const completedSRs = (bundle.entry?.map(e => e.resource).filter(Boolean) as ServiceRequest[]) || [];
          if (completedSRs.length === 0) {
            return of([] as ServiceMarginRow[]);
          }

          const computationObservables = completedSRs.map(sr => {
            const patientId = sr.subject?.reference?.replace('Patient/', '') || '';
            
            return this.clinicalPricingService.calculatePricingForServiceRequest(sr, patientId).pipe(
              switchMap(calcResult => {
                // If we have a cached patient, use it to avoid duplicate network requests
                if (!patientId) {
                  return of({ sr, calcResult, patientName: 'Unknown Patient', patientId });
                }
                
                if (this.patientCache.has(patientId)) {
                  const patient = this.patientCache.get(patientId)!;
                  const name = this.patientNamePipe.transform(patient.name);
                  return of({ sr, calcResult, patientName: name, patientId });
                }

                return this.http.get<Patient>(`${this.backendEndPoint}/Patient/${patientId}`).pipe(
                  map(patient => {
                    this.patientCache.set(patientId, patient);
                    const name = this.patientNamePipe.transform(patient.name);
                    return { sr, calcResult, patientName: name, patientId };
                  }),
                  catchError(() => {
                    const displayName = sr.subject?.display || `Patient #${patientId}`;
                    return of({ sr, calcResult, patientName: displayName, patientId });
                  })
                );
              }),
              map(({ sr, calcResult, patientName, patientId }) => {
                const row: ServiceMarginRow = {
                  serviceRequest: sr,
                  patientId,
                  patientName,
                  code: calcResult.serviceRequestCode,
                  display: calcResult.serviceRequestDisplay,
                  type: calcResult.serviceRequestType,
                  costPrice: calcResult.requiredConsumablesCost + calcResult.baseCost,
                  sellingPrice: calcResult.sellingPrice,
                  profit: calcResult.netProfit,
                  currency: calcResult.currency || 'NGN',
                  calcDetails: calcResult
                };
                return row;
              }),
              catchError(err => {
                console.error(`Calculation failed for ServiceRequest/${sr.id}`, err);
                const code = (sr.code?.coding?.[0]?.code ?? sr.code?.text ?? 'N/A').toString();
                const display = sr.code?.text ?? sr.code?.coding?.[0]?.display ?? code;
                return of({
                  serviceRequest: sr,
                  patientId,
                  patientName: sr.subject?.display || `Patient #${patientId}`,
                  code,
                  display,
                  type: 'others',
                  costPrice: 0,
                  sellingPrice: 0,
                  profit: 0,
                  currency: 'NGN',
                  calcDetails: {} as any
                } as ServiceMarginRow);
              })
            );
          });

          return forkJoin(computationObservables);
        })
      )
      .subscribe({
        next: (rows) => {
          this.servicesRows = rows;
          this.calculateServicesAggregates();
          this.servicesLoading = false;
          this.cdRef.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load completed service requests', err);
          this.servicesLoading = false;
          this.cdRef.markForCheck();
        }
      });
  }

  private calculateServicesAggregates() {
    this.totalServicesCount = this.servicesRows.length;
    this.totalServicesCost = this.servicesRows.reduce((acc, r) => acc + r.costPrice, 0);
    this.totalServicesRevenue = this.servicesRows.reduce((acc, r) => acc + r.sellingPrice, 0);
    this.totalServicesProfit = this.servicesRows.reduce((acc, r) => acc + r.profit, 0);
  }

  /**
   * Load Medication resources and associated ChargeItemDefinitions
   */
  loadPharmacyProducts() {
    if (this.productsLoading) return;
    this.productsLoading = true;
    this.productsRows = [];
    this.cdRef.markForCheck();

    forkJoin({
      invoices: this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
        resourceType: 'Invoice',
        urlSearchObject: {
          status: 'balanced',
          ...(this.activePeriodPreset !== 'all' ? {
            date: [`ge${this.toFhirDate(this.periodStartDate)}`, `le${this.toFhirDate(this.periodEndDate)}`]
          } : {})
        },
        scope: 'orgWide',
        count: 1000,
        tier1FollowNextLinks: true,
        tier1MaxPages: 50
      }),
      cids: this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItemDefinition>({
        resourceType: 'ChargeItemDefinition',
        scope: 'orgWide',
        count: 1000,
        tier1FollowNextLinks: true,
        tier1MaxPages: 50
      })
    }).pipe(
      switchMap(({ invoices, cids }) => {
        // Extract ChargeItem IDs
        const ciIds = new Set<string>();
        (invoices || []).forEach(inv => {
          (inv.lineItem || []).forEach((li: any) => {
            const ref = li.chargeItemReference?.reference || '';
            const id = ref.split('/').pop();
            if (id) ciIds.add(id);
          });
        });

        const ciIdArray = Array.from(ciIds);
        if (ciIdArray.length === 0) {
          return of({ invoices, cids, chargeItems: [], meds: [] });
        }

        const chunkArray = (arr: any[], size: number) => Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
        const ciChunks = chunkArray(ciIdArray, 100);
        
        const ciReqs = ciChunks.map(chunk => 
          this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
            resourceType: 'ChargeItem',
            urlSearchObject: { _id: chunk.join(',') },
            scope: 'orgWide',
            count: 1000,
            tier1FollowNextLinks: true,
            tier1MaxPages: 50
          })
        );

        return forkJoin(ciReqs).pipe(
          map(ciResults => {
            const chargeItems = ciResults.flat();
            return { invoices, cids, chargeItems };
          })
        );
      }),
      switchMap(({ invoices, cids, chargeItems }) => {
         // Extract Medication IDs
         const medIds = new Set<string>();
         (chargeItems || []).forEach(ci => {
           const ref = ci.productReference?.reference || '';
           if (ref.startsWith('Medication/')) {
             const id = ref.replace('Medication/', '');
             if (id) medIds.add(id);
           }
         });

         const medIdArray = Array.from(medIds);
         if (medIdArray.length === 0) {
           return of({ invoices, cids, chargeItems, meds: [] });
         }

         const chunkArray = (arr: any[], size: number) => Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
         const medChunks = chunkArray(medIdArray, 100);

         const medReqs = medChunks.map(chunk => 
           this.utilityService.threeTierQueryAndSyncToStateOnce$<Medication>({
             resourceType: 'Medication',
             urlSearchObject: { _id: chunk.join(',') },
             scope: 'orgWide',
             count: 1000,
             tier1FollowNextLinks: true,
             tier1MaxPages: 50
           })
         );

         return forkJoin(medReqs).pipe(
           map(medResults => {
             const meds = medResults.flat();
             return { invoices, cids, chargeItems, meds };
           })
         );
      })
    ).subscribe({
      next: ({ meds, cids, invoices, chargeItems }) => {
        const medicationsList = meds || [];
        const cidList = cids || [];
        const invoiceList = invoices || [];
        const chargeItemList = chargeItems || [];

        // Build maps of ChargeItemDefinitions indexed by Medication ID
        const cidMap = new Map<string, ChargeItemDefinition>();
        cidList.forEach(cid => {
          if (cid?.instance?.[0]?.reference?.startsWith('Medication/')
          && cid?.status === 'active'
          ) {
            const medId = cid.instance[0].reference.replace('Medication/', '');
            cidMap.set(medId, cid);
          }
        });

        // Pre-process ChargeItems: Map Medication ID -> Array of ChargeItems
        const chargeItemsByMedication = new Map<string, ChargeItem[]>();
        chargeItemList.forEach(ci => {
          const ref = ci.productReference?.reference || '';
          const medId = ref.split('/').pop();
          if (medId) {
            if (!chargeItemsByMedication.has(medId)) {
              chargeItemsByMedication.set(medId, []);
            }
            chargeItemsByMedication.get(medId)!.push(ci);
          }
        });

        // Pre-process Invoices: Map ChargeItem ID -> Array of Invoice LineItems (to handle duplicates safely)
        const paidChargeItemLineMap = new Map<string, any[]>();
        invoiceList.forEach(inv => {
          (inv.lineItem || []).forEach((li: any) => {
            const ref = (li?.chargeItemReference?.reference || '').toString();
            const ciId = ref.split('/').pop();
            if (ciId) {
              if (!paidChargeItemLineMap.has(ciId)) {
                paidChargeItemLineMap.set(ciId, []);
              }
              paidChargeItemLineMap.get(ciId)!.push(li);
            }
          });
        });

        const rows: ProductMarginRow[] = medicationsList.map(med => {
          const medId = med.id || '';
          const cid = cidMap.get(medId);

          const name = med.code?.text || med.code?.coding?.[0]?.display || 'Unnamed Medication';
          const form = med.form?.text || med.form?.coding?.[0]?.display || '';
          const manufacturer = med.manufacturer?.display || '';
          
          let currency = 'NGN';

          // Extract medication unit cost from ChargeItemDefinition total cost
          let unitCostPrice = 0;
          console.debug('Processing Medication', name, 'with CID', cid?.id || 'N/A', 'extensions count:', cid?.extension?.length || 0);
          const costExts = cid?.extension?.filter(e => e.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost' || e.url?.endsWith('/ChargeItemDefinition-totalCost')) || [];
          costExts.forEach(ext => {
            console.debug('Processing cost extension:', ext);
            const subs = ext.extension || [];
            const typeSub = subs.find(e => e.url === 'type');
            const amountSub = subs.find(e => e.url === 'amount');
            if (typeSub && amountSub) {
              if ((typeSub as any).valueCode === 'base') {
                unitCostPrice += ((amountSub as any).valueMoney?.value || 0);
              }
            } else if (amountSub) {
              // fallback if no type is explicitly defined but amount is present
              unitCostPrice += ((amountSub as any).valueMoney?.value || 0);
            }
          });

          // Extract standard selling price as fallback (summing price components)
          let unitSellingPrice = 0;
          const sellComponents = cid?.propertyGroup?.[0]?.priceComponent || [];
          if (sellComponents.length > 0) {
            let base = 0, surcharge = 0, tax = 0, deduction = 0, discount = 0;
            sellComponents.forEach((pc: any) => {
              const type = (pc.type || '').toString().trim().toLowerCase();
              const value = Number(pc.amount?.value) || 0;
              if (pc.amount?.currency) currency = pc.amount.currency;
              if (type === 'base') base += value;
              else if (type === 'surcharge') surcharge += value;
              else if (type === 'tax') tax += value;
              else if (type === 'deduction') deduction += value;
              else if (type === 'discount') discount += value;
            });
            unitSellingPrice = (base + surcharge + tax) - Math.abs(deduction) - Math.abs(discount);
          }

          // Match ChargeItems and balanced Invoices to calculate actual sales
          let unitsSold = 0;
          let totalCostOfSales = 0;
          let totalActualRevenue = 0;

          const myChargeItems = chargeItemsByMedication.get(medId) || [];

          myChargeItems.forEach(ci => {
            if (!ci.id) return;
            
            const matchedLineItems = paidChargeItemLineMap.get(ci.id) || [];
            
            matchedLineItems.forEach(li => {
              // Actual paid sale matched!
              const qty = ci.quantity?.value || 1;
              unitsSold += qty;

              // Parse paid price components
              const priceComponents = li.priceComponent || [];
              let sellingVal = 0;
              if (priceComponents.length > 0) {
                let base = 0, surcharge = 0, tax = 0, deduction = 0, discount = 0;
                priceComponents.forEach((pc: any) => {
                  const type = (pc.type || '').toString().trim().toLowerCase();
                  const value = Number(pc.amount?.value) || 0;
                  if (pc.amount?.currency) currency = pc.amount.currency;
                  if (type === 'base') base += value;
                  else if (type === 'surcharge') surcharge += value;
                  else if (type === 'tax') tax += value;
                  else if (type === 'deduction') deduction += value;
                  else if (type === 'discount') discount += value;
                });
                sellingVal = (base + surcharge + tax) - Math.abs(deduction) - Math.abs(discount);
              }

              if (sellingVal <= 0 && ci.priceOverride?.value !== undefined) {
                sellingVal = ci.priceOverride.value;
                if (ci.priceOverride.currency) currency = ci.priceOverride.currency;
              }

              if (sellingVal <= 0) {
                sellingVal = unitSellingPrice * qty;
              }

              totalActualRevenue += sellingVal;
              totalCostOfSales += unitCostPrice * qty;
            });
          });

          const totalProfit = totalActualRevenue - totalCostOfSales;

          return {
            medication: med,
            name,
            form,
            manufacturer,
            unitsSold,
            costPrice: totalCostOfSales,
            sellingPrice: totalActualRevenue,
            profit: totalProfit,
            currency
          };
        });

        this.productsRows = rows;
        this.calculateProductsAggregates();
        this.productsLoading = false;
        this.cdRef.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load pharmacy product actual sales data', err);
        this.productsLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  private calculateProductsAggregates() {
    this.totalProductsCount = this.productsRows.length;
    this.totalProductsSoldUnits = this.productsRows.reduce((acc, r) => acc + r.unitsSold, 0);
    this.totalProductsValuationCost = this.productsRows.reduce((acc, r) => acc + r.costPrice, 0);
    this.totalProductsValuationRevenue = this.productsRows.reduce((acc, r) => acc + r.sellingPrice, 0);
    this.totalProductsValuationProfit = this.productsRows.reduce((acc, r) => acc + r.profit, 0);
  }

  /**
   * Helper to format values as currency
   */
  formatCurrency(value: number, currencyCode: string = 'NGN'): string {
    const symbol = currencyCode === 'NGN' ? '₦' : currencyCode + ' ';
    return symbol + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * CSS Indicator for Profit Status
   */
  getProfitClass(profit: number): string {
    if (profit > 0) return 'positive-profit';
    if (profit < 0) return 'negative-profit';
    return 'zero-profit';
  }

  /**
   * Icon representation based on service request type
   */
  getServiceTypeIcon(type: string): string {
    switch (type?.toLowerCase()) {
      case 'laboratory': return 'biotech';
      case 'imaging': return 'image';
      case 'procedure': return 'healing';
      case 'surgery': return 'medical_services';
      default: return 'assignment';
    }
  }

  // --- Date Period Filtering ---

  private computeDateRange() {
    this.periodEndDate = this.timeService.getTrueDate();

    if (this.activePeriodPreset === 'custom' && this.customStartDate && this.customEndDate) {
      this.periodStartDate = this.customStartDate;
      this.periodEndDate = this.customEndDate;
    } else if (this.activePeriodPreset === 'today') {
      this.periodStartDate = this.timeService.getTrueDate();
      this.periodEndDate = this.timeService.getTrueDate();
    } else {
      const preset = this.periodPresets.find(p => p.id === this.activePeriodPreset);
      if (preset && preset.days > 0) {
        this.periodStartDate = this.timeService.getTrueDate();
        this.periodStartDate.setDate(this.periodStartDate.getDate() - preset.days);
      } else {
        // 'all' — no date filtering
        this.periodStartDate = new Date(2000, 0, 1);
      }
    }
  }

  private toFhirDate(date: Date): string {
    return this.timeService.toFhirDate(date);
  }

  getFormattedPeriodRange(): string {
    if (this.activePeriodPreset === 'all') return 'All available records';
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const start = this.periodStartDate.toLocaleDateString('en-US', options);
    const end = this.periodEndDate.toLocaleDateString('en-US', options);
    return `${start} — ${end}`;
  }

  onPeriodChanged(presetId: string) {
    if (presetId === this.activePeriodPreset) return;
    this.activePeriodPreset = presetId;
    this.showCustomRange = false;
    this.computeDateRange();
    this.reloadActiveTab();
  }

  onCustomDateApplied() {
    if (!this.customStartDate || !this.customEndDate) return;
    this.activePeriodPreset = 'custom';
    this.computeDateRange();
    this.showCustomRange = false;
    this.reloadActiveTab();
  }

  private reloadActiveTab() {
    if (this.activeTabId === 'services') {
      this.servicesLoading = false;
      this.loadCompletedServices();
    } else if (this.activeTabId === 'products') {
      this.productsLoading = false;
      this.loadPharmacyProducts();
    }
  }

  getDateSourceHint(): string {
    return this.activeTabId === 'services'
      ? 'Filtered by service completion date'
      : 'Filtered by invoice date';
  }

  toggleCustomRange() {
    this.showCustomRange = !this.showCustomRange;
  }
}
